// @ts-nocheck
import _, { add } from 'lodash';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  Box,
  Icon,
  TextField,
  Button,
  Typography,
  ListItem,
  List,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  InputAdornment,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useIfMounted } from '../../../../common/hooks';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
  },
  form: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControl-root': {
      minHeight: '78px',
    },
    '& .MuiList-padding': {
      paddingBottom: theme.spacing(2),
    },
    '& .MuiListItem-secondaryAction': {
      paddingRight: theme.spacing(8),
    },
  },
  formContent: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    padding: theme.spacing(1, 3),
    overflow: 'auto',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  button: {
    margin: theme.spacing(1),
    '&:last-child': {
      marginRight: '0',
    },
  },
}));

function ContractUsersForm(props) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [t] = useTranslation();
  const classes = useStyles();
  const ifMounted = useIfMounted();
  let emails = [];

  const {
    onAddUser,
    onSave,
    onCancel,
    actionsDisabled,
    cancelButtonText,
    cancelButtonIcon,
    saveButtonText,
    saveButtonIcon,
    addedUsers,
  } = props;

  const [users, setUsers] = useState(_.defaultTo(addedUsers, []));

  const validateEmailString = emailString => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailString);

  const validateEmail = value => {
    if (!value) {
      setError(t('user.userEmailEmpty'));
    } else if (value.indexOf(',') !== -1) {
      if (value.trim().endsWith(',')) {
        emails = value
          .trim()
          .slice(0, -1)
          .split(',')
          .map(x => x.trim());
      } else {
        emails = value.split(',').map(x => x.trim());
      }
      const invalidEmail = emails.find(x => validateEmailString(x));
      if (invalidEmail) {
        setError(`"${invalidEmail}" - ${t('user.userEmailInvalid')}`);
      } else {
        setError('');
        return true;
      }
    } else if (validateEmailString(value)) {
      setError(t('user.userEmailInvalid'));
    } else {
      setError('');
      return true;
    }
    return false;
  };

  const handleSubmit = async event => {
    event.preventDefault();

    const usersToAdd = users
      .filter(user => !addedUsers.some(addedUser => addedUser.userId === user.userId))
      .map(user => user.userId);
    const usersToRemove = addedUsers
      .filter(addedUser => !users.some(user => addedUser.userId === user.userId))
      .map(user => user.userId);

    const formDataToAdd = {
      ids: usersToAdd,
      action: 'ADD',
    };
    const formDataToRemove = {
      ids: usersToRemove,
      action: 'REMOVE',
    };

    setSubmitting(true);
    try {
      if (usersToAdd.length) await onSave(formDataToAdd);
      if (usersToRemove.length) await onSave(formDataToRemove);
    } catch (e) {
      // Error already handled
    } finally {
      ifMounted(() => setSubmitting(false));
    }
  };

  const handleAddUser = async () => {
    const valid = validateEmail(email);
    if (valid) {
      setLoading(true);
      if (emails.length > 0) {
        const dataSet = [];
        await Promise.all(
          emails.map(async e => {
            await onAddUser(e).then(res => dataSet.push(res));
          })
        );
        ifMounted(() => {
          let errorText, emailText;
          const success = dataSet
            .filter(data => data.response && data.response.length > 0 && data.isAllowed &&
              data.response.users.every(user => user.status.toLowerCase() === 'aktiv'))
            .map(x => x.response.users[0]);
          
          const notFound = dataSet
            .filter(data => data.response && data.response.length === 0)
            .map(x => x.email.trim());

          const notAllowed = dataSet.filter(data => !data.isAllowed).map(x => x.email.trim());
          
          const notActive = dataSet
            .filter(data => data.response.users.every(user => 
              !success.some(excelente => excelente.userId === user.userId)) &&
              !notFound.some(lost => lost === data.email) &&
              !notAllowed.some(underContract => underContract === data.email))
            .map(x => x.email.trim());
          
          if (success.length) {
            setUsers(prevUsers => _.uniqBy(prevUsers.concat(success), 'userId'));
          }
          if (notFound.length) {
            errorText = `"${notFound.join('", "')}" - ${t('user.userNotFound')}`;
            emailText = notFound.join(',');
          }
          if (notAllowed.length) {
            errorText = (errorText ? `${errorText}\n\r` : '') + `"${notAllowed.join('", "')}" - ${t('user.userContractAssigned')}`;
            emailText = (emailText ? `${emailText}, ` : '') + notAllowed.join(',');
          }
          if (notActive.length) {
            setError((errorText ? `${errorText}\n\r` : '') + `"${notActive.join('", "')}" - ${t('user.userNotActive')}`);
            setEmail((emailText ? `${emailText}, ` : '') + notActive.join(','));
          }
          setLoading(false);
        });
      } else {
        const data = await onAddUser(email);
        ifMounted(() => {
          if (data.response && data.response.length) {
            if(!data.response.users.every(async user => user.status.toLowerCase() === 'aktiv')) {
              setError(t('user.userNotActive'));
            } else if (!data.isAllowed) {
              setError(t('user.userContractAssigned'));
            } else {
              setUsers(prevUsers => _.uniqBy(prevUsers.concat(data.response.users[0]), 'userId'));
              setEmail('');
              setError(null);
            }
          } else {
            setError(t('user.userNotFound'));
          }
          setLoading(false);
        });
      }
    }
  };

  const handleRemoveUser = userId => {
    setUsers(prevUsers => prevUsers.filter(user => user.userId !== userId));
  };

  const handleEmailChange = event => {
    const { value } = event.target;
    validateEmail(value);
    setEmail(value);
  };

  const handleKeyPress = event => {
    if (_.isEmpty(email) || !_.isEmpty(error)) {
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddUser();
    }
  };

  return (
    <Box id="ContractUsersForm-container" className={classes.container}>
      <form id="ContractUsersForm-form" className={classes.form} onSubmit={handleSubmit}>
        <Box className={classes.formContent}>
          <Typography>{t('contract.contractActivateInfo')}</Typography>
          <List id="ContractUsersForm-userList">
            {users.map(user => (
              <ListItem key={user.userId} disableGutters>
                <ListItemAvatar>
                  <Avatar>
                    <Icon>person</Icon>
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${t('user.userId')}: ${user.userId}`}
                  secondary={`${t('user.userEmail')}: ${user.email}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="remove"
                    onClick={() => handleRemoveUser(user.userId)}
                  >
                    <Icon>remove</Icon>
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          <Tooltip title={t('user.userEmailCsv')}>
            <TextField
              id="ContractUsersForm-userEmail"
              label={t('user.userEmail')}
              variant="outlined"
              value={email}
              onChange={handleEmailChange}
              onKeyPress={handleKeyPress}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <IconButton
                        edge="end"
                        aria-label="add"
                        onClick={handleAddUser}
                        disabled={_.isEmpty(email) || !_.isEmpty(error)}
                      >
                        <Icon>add</Icon>
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
              error={!_.isEmpty(error)}
              helperText={error}
              fullWidth
            />
          </Tooltip>
        </Box>
        <Box className={classes.actions}>
          <Button
            id="ContractUsersForm-cancel"
            className={classes.button}
            startIcon={<Icon>{_.defaultTo(cancelButtonIcon, 'cancel')}</Icon>}
            onClick={onCancel}
            disabled={actionsDisabled || submitting}
          >
            <Typography variant="button" noWrap>
              {_.defaultTo(cancelButtonText, t('common.cancel'))}
            </Typography>
          </Button>
          <Button
            id="ContractUsersForm-save"
            type="submit"
            color="primary"
            className={classes.button}
            startIcon={<Icon>{_.defaultTo(saveButtonIcon, 'save')}</Icon>}
            disabled={actionsDisabled || submitting || (_.isEmpty(users) && _.isEmpty(addedUsers))}
          >
            <Typography variant="button" noWrap>
              {_.defaultTo(saveButtonText, t('common.save'))}
            </Typography>
          </Button>
        </Box>
      </form>
    </Box>
  );
}

ContractUsersForm.defaultProps = {
  cancelButtonText: undefined,
  cancelButtonIcon: undefined,
  saveButtonText: undefined,
  saveButtonIcon: undefined,
  actionsDisabled: undefined,
  addedUsers: [],
};

ContractUsersForm.propTypes = {
  cancelButtonText: PropTypes.string,
  cancelButtonIcon: PropTypes.string,
  saveButtonText: PropTypes.string,
  saveButtonIcon: PropTypes.string,
  onAddUser: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool,
  addedUsers: PropTypes.arrayOf(PropTypes.object),
};

export default ContractUsersForm;
